import { useCallback } from "react";
import { useNavigate } from "react-router-dom-v5-compat";

import type { EntityRecordResponse } from "@btnet/services-btnet/handlers/models/entityRecordResponse";
import type { SpecificationResponse } from "@btnet/services-btnet/handlers/models/specificationResponse";

import { useRouteProps } from "utilities/url/url";

import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { RELATED_ENTITIES_PREFIX } from "commonComponents/entity/entityLinkSelector/EntityLinkRoutes.utilities";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";

import { toEntityRecord } from "entity/specification/SpecificationLinkSelectorV2/SpecificationLinkSelectorV2.utilities";
import { RouteSpecification } from "entity/specification/SpecificationRoute";

interface IEntityLinkRouteProps {
    /**
     * Callback when an entity is deleted
     * @param externalId - The external ID (GUID) of the deleted entity
     */
    onDeleted: (externalId: string) => void;
    /**
     * Callback when an entity is edited/saved
     */
    onEdited: (entity: EntityRecordResponse) => void;
}

/**
 * Centralized routing for related entity modals in EntityLinkSelector
 */
export const EntityLinkRoutes = ({ onDeleted, onEdited }: IEntityLinkRouteProps) => {
    const { match } = useRouteProps();
    const navigate = useNavigate();

    const modalConfig: IModalConfiguration = {
        beforeClose: () => navigate(-1),
        parentRoute: match.url,
    };

    // Adapter to convert SpecificationResponse to externalId for generic onDeleted callback
    const handleSpecificationDeleted = useCallback(
        (spec: SpecificationResponse) => {
            onDeleted(spec.externalId);
        },
        [onDeleted],
    );

    const handleSpecificationEdited = useCallback(
        (spec: SpecificationResponse) => {
            onEdited(toEntityRecord(spec));
        },
        [onEdited],
    );

    return (
        <RouteRelative
            path={`${RELATED_ENTITIES_PREFIX}`}
            render={() => (
                <>
                    {/*
                     * All entity detail routes are under the /related-entities prefix
                     * Each entity defines its own path pattern in EntityLinkRoutes.utilities.ts
                     */}
                    <RouteSpecification
                        modalConfig={modalConfig}
                        stopClickPropagation
                        onDelete={handleSpecificationDeleted}
                        onEdit={handleSpecificationEdited}
                    />

                    {/* Future entity routes added here */}
                    {/* Example:
                        <RouteRfi modalConfig={modalConfig} />
                    */}
                </>
            )}
        />
    );
};
